import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import axiosAuth from '../../config/axios';
import { IconShield } from '../../components/Icons';
import { PRIORIDAD, fecha, estilos as s } from './comun';

// Señales tempranas: cambios registrados en Playfesor que conviene revisar.
// No son diagnósticos ni etiquetas: el orientador decide si remitir o descartar.

const FILTROS = [
  { valor: 'pendientes', nombre: 'Por revisar' },
  { valor: 'remitidas', nombre: 'Remitidas' },
  { valor: 'descartadas', nombre: 'Descartadas' },
];

const ESTADO = {
  nueva:      { nombre: 'Nueva',      color: '#b45309', fondo: '#fff7e6' },
  revisada:   { nombre: 'Revisada',   color: '#1565c0', fondo: '#e3f2fd' },
  remitida:   { nombre: 'Remitida',   color: '#2e7d32', fondo: '#e8f5e9' },
  descartada: { nombre: 'Descartada', color: '#6b7280', fondo: '#f1f2f4' },
};

function Regla({ u }) {
  if (!u) return null;
  return (
    <ul style={es.reglas}>
      <li>Riesgo académico alto o crítico en {u.materias_riesgo} o más materias (Motor de Riesgo).</li>
      <li>Promedio de los últimos 30 días {String(u.descenso_promedio).replace('.', ',')} puntos o más por debajo de los 30 anteriores.</li>
      <li>{u.ausencias} o más ausencias no justificadas en 30 días, o un aumento de 50 % frente al mes anterior.</li>
      <li>{u.anotaciones_mejora} o más anotaciones de mejora en 30 días.</li>
      <li>{u.actividades_vencidas} o más actividades cerradas sin entregar.</li>
      <li>Una señal descartada no vuelve a aparecer en {u.dias_silencio} días, salvo que la situación empeore.</li>
    </ul>
  );
}

export default function Senales() {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('pendientes');
  const [senales, setSenales] = useState([]);
  const [umbrales, setUmbrales] = useState(null);
  const [esLider, setEsLider] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);           // { senal, accion: 'descartar' | 'remitir' }
  const [motivo, setMotivo] = useState('');
  const [prioridad, setPrioridad] = useState('media');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const [error, setError] = useState('');
  const [verReglas, setVerReglas] = useState(false);

  useEffect(() => {
    axiosAuth.get('/api/bienestar/estado').then(r => setEsLider(r.data.data?.nivel === 'lider')).catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await axiosAuth.get(`/api/bienestar/senales?estado=${filtro}`);
      setSenales(r.data.data || []);
      setUmbrales(r.data.umbrales || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las señales');
    } finally {
      setCargando(false);
    }
  }, [filtro]);
  useEffect(() => { cargar(); }, [cargar]);

  const avisar = (t) => { setAviso(t); setTimeout(() => setAviso(''), 4000); };

  async function accion(senal, cuerpo) {
    setOcupado(true); setError('');
    try {
      const r = await axiosAuth.patch(`/api/bienestar/senales/${senal.id}`, cuerpo);
      setModal(null);
      avisar(r.data.mensaje);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo completar la acción');
    } finally {
      setOcupado(false);
    }
  }

  async function recalcular() {
    setOcupado(true); setError('');
    try {
      const r = await axiosAuth.post('/api/bienestar/senales/recalcular');
      const d = r.data.data;
      avisar(`Listo: ${d.nuevas} nuevas, ${d.actualizadas} actualizadas, ${d.resueltas} resueltas.`);
      cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo recalcular');
    } finally {
      setOcupado(false);
    }
  }

  function abrir(senal, tipo) {
    setMotivo(''); setPrioridad('media'); setError('');
    setModal({ senal, accion: tipo });
  }

  return (
    <div style={s.pagina}>
      <Navbar titulo="Señales tempranas" />
      <div style={es.contenido}>
        <div style={es.intro}>
          <IconShield size={18} style={{ flexShrink: 0, color: '#5a4fcf' }} />
          <div>
            Cambios recientes registrados en Playfesor (notas, asistencia, anotaciones y entregas) que conviene revisar.
            <strong> No son diagnósticos ni etiquetas</strong>: solo hechos con números. Tú decides si remitir o descartar.
            Solo el equipo de orientación ve esta sección.{' '}
            <button onClick={() => setVerReglas(!verReglas)} style={es.enlace}>{verReglas ? 'Ocultar reglas' : '¿Cómo se calculan?'}</button>
            {verReglas && <Regla u={umbrales} />}
          </div>
        </div>

        <div style={es.barra}>
          <div style={es.selector} role="tablist">
            {FILTROS.map(f => (
              <button key={f.valor} role="tab" aria-selected={filtro === f.valor} onClick={() => setFiltro(f.valor)}
                style={{ ...es.opcion, ...(filtro === f.valor ? es.opcionActiva : {}) }}>{f.nombre}</button>
            ))}
          </div>
          {esLider && (
            <button onClick={recalcular} disabled={ocupado} style={s.btnSecundario} title="Se calculan solas cada noche">
              Recalcular ahora
            </button>
          )}
        </div>

        {aviso && <div style={s.exito}>{aviso}</div>}
        {error && !modal && <div style={s.errorBox}>{error}</div>}
        {cargando && <p style={s.gris}>Cargando...</p>}
        {!cargando && senales.length === 0 && (
          <div style={es.vacio}>{filtro === 'pendientes' ? 'No hay señales por revisar.' : 'No hay señales en esta lista.'}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {senales.map(sn => {
            const pendiente = ['nueva', 'revisada'].includes(sn.estado);
            return (
              <div key={sn.id} style={s.card}>
                <div style={es.cabecera}>
                  <div style={{ minWidth: 0 }}>
                    <div style={es.nombre}>{sn.estudiante}</div>
                    <div style={es.meta}>
                      {sn.grado ? `${sn.grado}° ${sn.grupo} · ` : ''}Detectada {fecha(sn.detectada_en)}
                      {sn.revisada_por ? ` · ${sn.estado === 'revisada' ? 'Revisada' : 'Atendida'} por ${sn.revisada_por}` : ''}
                    </div>
                  </div>
                  <span style={{ ...es.chip, color: ESTADO[sn.estado].color, background: ESTADO[sn.estado].fondo }}>
                    {sn.resuelta_sola ? 'Se normalizó' : ESTADO[sn.estado].nombre}
                  </span>
                </div>
                <div style={es.tipos}>
                  {sn.tipos.map(t => <span key={t.codigo} style={es.tipo}>{t.nombre}</span>)}
                </div>
                <p style={es.texto}>{sn.texto}</p>
                {sn.motivo_descarte && <p style={es.descarte}>Motivo del descarte: {sn.motivo_descarte}</p>}
                {sn.resuelta_sola && <p style={es.descarte}>Los datos volvieron a la normalidad antes de que se revisara.</p>}

                {pendiente && sn.caso_abierto_id && (
                  <div style={es.nota}>
                    Este estudiante ya tiene un caso abierto.{' '}
                    <button onClick={() => navigate(`/bienestar/casos/${sn.caso_abierto_id}`)} style={es.enlace}>Ver caso</button>
                  </div>
                )}
                {pendiente && !sn.caso_abierto_id && sn.remision_pendiente_id && (
                  <div style={es.nota}>
                    Ya hay una remisión de este estudiante en la bandeja.{' '}
                    <button onClick={() => navigate('/bienestar/remisiones')} style={es.enlace}>Ir a remisiones</button>
                  </div>
                )}

                {pendiente && (
                  <div style={es.acciones}>
                    {sn.estado === 'nueva' && (
                      <button onClick={() => accion(sn, { accion: 'revisar' })} disabled={ocupado} style={s.btnSecundario}>Marcar como revisada</button>
                    )}
                    <button onClick={() => abrir(sn, 'descartar')} style={s.btnSecundario}>Descartar</button>
                    {!sn.caso_abierto_id && (
                      <button onClick={() => abrir(sn, 'remitir')} style={s.btnPrimario}>Remitir a la bandeja</button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {modal && (
        <>
          <div style={s.fondoModal} onClick={() => setModal(null)} />
          <div style={s.modal} role="dialog" aria-label={modal.accion === 'descartar' ? 'Descartar señal' : 'Remitir señal'}>
            <h3 style={s.modalTitulo}>{modal.accion === 'descartar' ? 'Descartar señal' : 'Remitir a la bandeja de orientación'}</h3>
            <p style={es.modalTexto}><strong>{modal.senal.estudiante}</strong></p>
            {modal.accion === 'descartar' ? (
              <div style={s.campo}>
                <label style={s.label}>¿Por qué se descarta? *</label>
                <textarea style={{ ...s.input, minHeight: '80px', resize: 'vertical' }} maxLength={1000} value={motivo}
                  onChange={e => setMotivo(e.target.value)} placeholder="Ej.: las ausencias corresponden a un viaje familiar avisado al colegio." />
                <p style={es.pista}>No volverá a aparecer en 30 días, salvo que la situación empeore.</p>
              </div>
            ) : (
              <>
                <p style={es.modalTexto}>
                  Se creará una remisión marcada como “Señal automática” con el texto de la señal. Desde la bandeja
                  se puede abrir el caso o archivar.
                </p>
                <div style={s.campo}>
                  <label style={s.label}>Prioridad</label>
                  <select style={{ ...s.input, maxWidth: '220px' }} value={prioridad} onChange={e => setPrioridad(e.target.value)}>
                    {Object.entries(PRIORIDAD).map(([k, v]) => <option key={k} value={k}>{v.nombre}</option>)}
                  </select>
                </div>
              </>
            )}
            {error && <div style={s.errorBox}>{error}</div>}
            <div style={s.botones}>
              <button onClick={() => setModal(null)} style={s.btnSecundario}>Cancelar</button>
              {modal.accion === 'descartar' ? (
                <button onClick={() => accion(modal.senal, { accion: 'descartar', motivo })} disabled={ocupado || !motivo.trim()}
                  style={{ ...s.btnPrimario, opacity: motivo.trim() ? 1 : 0.5 }}>Descartar</button>
              ) : (
                <button onClick={() => accion(modal.senal, { accion: 'remitir', prioridad })} disabled={ocupado} style={s.btnPrimario}>Remitir</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const es = {
  contenido: { padding: '24px', maxWidth: '950px', margin: '0 auto', width: '100%' },
  intro: { display: 'flex', gap: '10px', alignItems: 'flex-start', background: '#f3f1ff', border: '1px solid #e0dcff', color: '#4a4380', borderRadius: '12px', padding: '12px 16px', fontSize: '13px', lineHeight: 1.55, marginBottom: '16px' },
  reglas: { margin: '8px 0 0', paddingLeft: '18px' },
  enlace: { background: 'none', border: 'none', color: '#667eea', cursor: 'pointer', fontSize: 'inherit', fontWeight: 700, padding: 0, fontFamily: 'inherit' },
  barra: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' },
  selector: { display: 'flex', background: '#fff', borderRadius: '999px', padding: '3px', border: '1px solid #e6e8f0', flexWrap: 'wrap' },
  opcion: { background: 'none', border: 'none', borderRadius: '999px', padding: '6px 14px', fontSize: '13px', fontWeight: 600, color: '#666', cursor: 'pointer', fontFamily: 'inherit' },
  opcionActiva: { background: '#667eea', color: '#fff' },
  vacio: { background: '#fff', border: '1px dashed #dde0f0', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#999', fontSize: '14px' },
  cabecera: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' },
  nombre: { fontSize: '16px', fontWeight: 800, color: '#1a1a2e' },
  meta: { fontSize: '12.5px', color: '#999', marginTop: '2px' },
  chip: { borderRadius: '999px', padding: '3px 10px', fontSize: '11.5px', fontWeight: 700, whiteSpace: 'nowrap' },
  tipos: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' },
  tipo: { background: '#f0f2f5', color: '#555', borderRadius: '999px', padding: '3px 10px', fontSize: '11.5px', fontWeight: 600 },
  texto: { fontSize: '14px', color: '#333', lineHeight: 1.6, margin: '10px 0 0' },
  descarte: { fontSize: '13px', color: '#888', margin: '8px 0 0', fontStyle: 'italic' },
  nota: { background: '#fff8e1', color: '#8a5a00', borderRadius: '8px', padding: '8px 12px', fontSize: '13px', marginTop: '10px' },
  acciones: { display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '14px' },
  modalTexto: { fontSize: '14px', color: '#555', lineHeight: 1.5, margin: '0 0 12px' },
  pista: { fontSize: '12px', color: '#999', margin: '6px 0 0' },
};
